import cx from "classnames";
import { initiateDownload } from "components/common/DownloadLink.tsx";
import Table from "components/Table/Table";
import { commonColumnProps, PAGE_SIZE } from "constants/TableConstants";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { setShowReportsTableDrawer } from "reducers/drawer";
import { useGetReportsQuery } from "services/reporting-api";
import { DATE_AND_TIME_FORMAT } from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";

const formatDate = (dateString: string) => {
  return format(parseISO(dateString), DATE_AND_TIME_FORMAT);
};

type ReportTableDrawerProps = {
  rreId?: number;
};

// type for the rows in the table
type ReportTableDataType = {
  id: number;
  rre: number;
  rre_name: string;
  status: string;
  report_type: string;
  created: string;
  start_time: string;
  end_time: string | null;
  filename: string | null;
  status_verbose: string;
  report_type_verbose: string;
  download_url: string | null; // store the original document for actions
};

export const ReportTableDrawer = ({ rreId }: ReportTableDrawerProps) => {
  const accessToken = useSelector((state) => state.main.accessToken);
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);

  const { data: reports = { results: [], count: 0 }, isLoading } =
    useGetReportsQuery({
      filters: {
        rre_id: rreId?.toString(),
      },
    });

  const showReportsTableDrawer = useSelector(
    (state) => state.drawer.showReportsTableDrawer,
  );

  // transform documents to table rows
  const rows: ReportTableDataType[] = (
    reports as unknown as { results: ReportTableDataType[] }
  ).results.map((report: ReportTableDataType) => ({
    id: report.id,
    filename: report.filename,
    rre_name: report.rre_name,
    report_type_verbose: report.report_type_verbose,
    status_verbose: report.status_verbose,
    created: report.created,
    download_url: report.download_url,
    rre: report.rre,
    status: report.status,
    report_type: report.report_type,
    start_time: report.start_time,
    end_time: report.end_time,
  }));

  const columns = [
    {
      key: "filename",
      name: "Name",
      ...commonColumnProps,
    },
    {
      key: "rre_name",
      name: "RRE",
      ...commonColumnProps,
    },
    {
      key: "created",
      name: "Created",
      ...commonColumnProps,
      renderCell: ({ row }: { row: ReportTableDataType }) => (
        <div>{formatDate(row.created)}</div>
      ),
    },
    {
      key: "report_type_verbose",
      name: "Type",
      ...commonColumnProps,
    },
    {
      key: "status_verbose",
      name: "Status",
      ...commonColumnProps,
    },
    {
      key: "actions",
      name: "Actions",
      ...commonColumnProps,
      width: "135px",
      renderCell: (props: { row: ReportTableDataType }) => (
        <div className="flex jc-se no-pd action-buttons">
          <div
            className="material-icons edit"
            title="Download"
            onClick={(e) => {
              e.preventDefault();
              if (!props.row.download_url) {
                return;
              }
              initiateDownload(
                props.row.download_url,
                accessToken,
                props.row.filename,
              );
            }}
          >
            archive
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      className={cx("documents documents-modal flex jc-se", {
        open: showReportsTableDrawer,
      })}
    >
      <div className="documents-wrapper w-search">
        <div className="header-primary flex jc-sb">
          Reports
          <a
            className="header-primary alt-btn"
            onClick={() => dispatch(setShowReportsTableDrawer(false))}
          >
            Close
          </a>
        </div>

        <div className="data-values mrg-top">
          <Table
            className="reports-table"
            columns={columns}
            rows={rows}
            rowKeyGetter={(row: ReportTableDataType) => row.id}
            onCellClick={() => {}}
            onSelectedRowsChange={() => {}}
            fullHeight
            pagination={{
              pageSize: PAGE_SIZE,
              pageIndex: page,
              rowCount: (reports as unknown as { count: number }).count,
              onPageChange: (pageIndex) => setPage(pageIndex),
              isLoading: isLoading,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportTableDrawer;
